import { TOTAL_KEYS } from './constants';

// Piano key layout: 88 keys, A0 to C8
// Pattern of white/black keys repeating every 12 semitones:
// A  A# B  C  C# D  D# E  F  F# G  G#
// W  B  W  W  B  W  B  W  W  B  W  B

const KEY_PATTERN = [
  true,
  false,
  true,
  true,
  false,
  true,
  false,
  true,
  true,
  false,
  true,
  false,
]; // true = white, false = black

export interface PianoKeyInfo {
  keyNum: number;
  isWhite: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PianoRenderer {
  render(container: HTMLElement): void;
  updateState(currentKey: number, completedCount: number): void;
  scrollToKey(keyNum: number): void;
  highlightKey(keyNum: number): void;
}

export function createPianoRenderer(): PianoRenderer {
  let svgEl: SVGSVGElement | null = null;
  let containerEl: HTMLElement | null = null;
  const keyElements: Map<number, SVGRectElement> = new Map();

  const WHITE_KEY_WIDTH = 24;
  const WHITE_KEY_HEIGHT = 120;
  const BLACK_KEY_WIDTH = 14;
  const BLACK_KEY_HEIGHT = 72;
  const PADDING = 4;

  // Build key info
  const keys: PianoKeyInfo[] = [];
  let whiteKeyIndex = 0;

  for (let i = 1; i <= TOTAL_KEYS; i++) {
    const patternIndex = (i - 1) % 12;
    const isWhite = KEY_PATTERN[patternIndex];

    if (isWhite) {
      keys.push({
        keyNum: i,
        isWhite: true,
        x: PADDING + whiteKeyIndex * WHITE_KEY_WIDTH,
        y: PADDING,
        width: WHITE_KEY_WIDTH - 1,
        height: WHITE_KEY_HEIGHT,
      });
      whiteKeyIndex++;
    } else {
      // Black key centered over the gap between white keys
      const lastWhiteX = PADDING + (whiteKeyIndex - 1) * WHITE_KEY_WIDTH;
      keys.push({
        keyNum: i,
        isWhite: false,
        x: lastWhiteX + WHITE_KEY_WIDTH - BLACK_KEY_WIDTH / 2 - 0.5,
        y: PADDING,
        width: BLACK_KEY_WIDTH,
        height: BLACK_KEY_HEIGHT,
      });
    }
  }

  const totalWidth = PADDING * 2 + whiteKeyIndex * WHITE_KEY_WIDTH;
  const totalHeight = PADDING * 2 + WHITE_KEY_HEIGHT;

  function render(container: HTMLElement): void {
    containerEl = container;
    container.innerHTML = '';

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${totalWidth} ${totalHeight}`);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.style.width = `${totalWidth}px`;
    svg.style.maxHeight = '100%';

    // Draw white keys first
    for (const key of keys) {
      if (!key.isWhite) continue;
      const rect = createKeyRect(key, 'white');
      svg.appendChild(rect);
      keyElements.set(key.keyNum, rect);
    }

    // Draw black keys on top
    for (const key of keys) {
      if (key.isWhite) continue;
      const rect = createKeyRect(key, 'black');
      svg.appendChild(rect);
      keyElements.set(key.keyNum, rect);
    }

    container.appendChild(svg);
    svgEl = svg;
  }

  function createKeyRect(
    key: PianoKeyInfo,
    type: 'white' | 'black',
  ): SVGRectElement {
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('x', String(key.x));
    rect.setAttribute('y', String(key.y));
    rect.setAttribute('width', String(key.width));
    rect.setAttribute('height', String(key.height));
    rect.setAttribute('rx', '2');
    rect.setAttribute('data-key', String(key.keyNum));

    if (type === 'white') {
      rect.setAttribute('fill', '#f8fafc');
      rect.setAttribute('stroke', '#cbd5e1');
      rect.setAttribute('stroke-width', '0.5');
    } else {
      rect.setAttribute('fill', '#1e293b');
      rect.setAttribute('stroke', '#0f172a');
      rect.setAttribute('stroke-width', '0.5');
    }

    return rect;
  }

  function updateState(currentKey: number, completedCount: number): void {
    for (const [keyNum, rect] of keyElements) {
      const keyInfo = keys.find((k) => k.keyNum === keyNum)!;
      const isWhite = keyInfo.isWhite;

      if (keyNum < currentKey) {
        // Completed
        rect.setAttribute('fill', isWhite ? '#86efac' : '#166534');
        rect.classList.remove('key-active-glow');
      } else if (keyNum === currentKey) {
        // Active
        rect.setAttribute('fill', isWhite ? '#3b82f6' : '#2563eb');
        rect.classList.add('key-active-glow');
      } else {
        // Upcoming
        rect.setAttribute('fill', isWhite ? '#f8fafc' : '#1e293b');
        rect.classList.remove('key-active-glow');
      }
    }
  }

  function scrollToKey(keyNum: number): void {
    if (!svgEl || !containerEl) return;

    const keyInfo = keys.find((k) => k.keyNum === keyNum);
    if (!keyInfo) return;

    // Calculate viewBox to center on this key region
    const containerWidth = containerEl.clientWidth;
    // Map container width to SVG units
    const svgUnitsVisible = containerWidth * (totalWidth / svgEl.clientWidth);
    const viewX = Math.max(
      0,
      Math.min(
        keyInfo.x + keyInfo.width / 2 - svgUnitsVisible / 2,
        totalWidth - svgUnitsVisible,
      ),
    );

    svgEl.setAttribute(
      'viewBox',
      `${viewX} 0 ${svgUnitsVisible} ${totalHeight}`,
    );
  }

  function highlightKey(keyNum: number): void {
    const rect = keyElements.get(keyNum);
    if (!rect) return;

    // Brief flash effect
    const origFill = rect.getAttribute('fill')!;
    rect.setAttribute('fill', '#fbbf24'); // Gold flash
    setTimeout(() => {
      // Will be overwritten by updateState on next cycle, but set completed color
      const keyInfo = keys.find((k) => k.keyNum === keyNum)!;
      rect.setAttribute('fill', keyInfo.isWhite ? '#86efac' : '#166534');
    }, 200);
  }

  return { render, updateState, scrollToKey, highlightKey };
}
